import React, { FC, useCallback, useEffect, useContext, useRef, useState } from 'react'
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { PrimaryConfirmModal } from '../../../molecules/ModalContainer/PrimaryDialogTemplate/PrimaryConfirmDialog'
import { ModalsContext } from '../../../../contexts'
import { hooks } from '../../../../libs'
import { forceLogout } from '../../../../stores/auth/auth.action'
import {
  tolOtacMaintenanceApi,
  TolOperationsApiError,
  isTolOperationsNetworkError,
} from '../../../../libs/api/tolOperations'
import { TolOtacMaintenance, ValidationError, ValidationErrorResponse } from '../../../../types/tolOperations'
import { OtacMaintenanceListPage } from './OtacMaintenanceListPage'
import { OtacMaintenanceFormPage } from './OtacMaintenanceFormPage'
import { sortOtacByStartAt } from './sortOtacSchedules'

export const OtacMaintenance: FC = () => {
  const history = useHistory()
  const location = useLocation()
  const dispatch = useDispatch()
  const { accessToken } = hooks.useAuth()
  const { hasOtacMaintenancePermission } = hooks.useTolOperation()
  const { appendModal, closeAllModals } = useContext(ModalsContext)

  // refを使う理由: モーダルは毎レンダーで再生成されるため、直接依存に入れると
  // エラーモーダル表示→再レンダー→再fetchの無限ループになる。refで安定化して防ぐ。
  const appendModalRef = useRef(appendModal)
  const closeAllModalsRef = useRef(closeAllModals)

  useEffect(() => {
    appendModalRef.current = appendModal
    closeAllModalsRef.current = closeAllModals
  }, [appendModal, closeAllModals])

  const [schedules, setSchedules] = useState<TolOtacMaintenance[]>([])
  const [serverErrors, setServerErrors] = useState<ValidationError[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)

  const showFetchErrorModal = useCallback(() => {
    appendModalRef.current(
      <PrimaryConfirmModal
        header="OTACメンテナンススケジュール一覧の取得に失敗しました。"
        content="再読み込みしてください。"
        label="再読み込み"
        skipButton={false}
        onSubmit={() => {
          window.location.reload()
        }}
      />,
    )
  }, [])

  const showSaveErrorModal = useCallback(() => {
    appendModalRef.current(
      <PrimaryConfirmModal
        header="OTACメンテナンススケジュールの保存に失敗しました。"
        content="時間をおいて再度お試しください。"
        label="閉じる"
        skipButton={false}
        onSubmit={() => {
          closeAllModalsRef.current()
        }}
      />,
    )
  }, [])

  const fetchSchedules = useCallback(async () => {
    if (!accessToken) return

    try {
      setIsLoading(true)
      const data = await tolOtacMaintenanceApi.getList(accessToken)
      // 並び順は編集画面でJSON順のまま使うため、取得結果はソートせず保持する
      setSchedules(Array.isArray(data) ? data : [])
    } catch (error) {
      if (isTolOperationsNetworkError(error)) {
        dispatch(forceLogout(true))
        return
      }
      const apiError = error as TolOperationsApiError
      // 404はスケジュール未登録（初回）のため空一覧として扱う
      if (apiError.response?.status === 404) {
        setSchedules([])
      } else {
        showFetchErrorModal()
      }
    } finally {
      setIsLoading(false)
    }
  }, [accessToken, dispatch, showFetchErrorModal])

  useEffect(() => {
    if (!hasOtacMaintenancePermission) return
    fetchSchedules()
  }, [hasOtacMaintenancePermission, fetchSchedules])

  // 保存成功後に一覧画面へ戻った際、state.refresh を検知して一覧を再取得する
  useEffect(() => {
    if (!hasOtacMaintenancePermission) return

    const state = location.state as { refresh?: boolean } | undefined
    if (!state?.refresh) return

    fetchSchedules()
    history.replace({ pathname: location.pathname, state: {} })
  }, [hasOtacMaintenancePermission, location, history, fetchSchedules])

  const clearServerErrors = useCallback(() => {
    setServerErrors(prev => (prev.length === 0 ? prev : []))
  }, [])

  if (!hasOtacMaintenancePermission) {
    return <Redirect to="/unauthorized" />
  }

  const handleSave = async (items: TolOtacMaintenance[]) => {
    if (!accessToken) return

    setIsSaving(true)
    setServerErrors([])
    try {
      await tolOtacMaintenanceApi.save(accessToken, items)
    } catch (error) {
      if (isTolOperationsNetworkError(error)) {
        dispatch(forceLogout(true))
        return
      }
      const apiError = error as TolOperationsApiError
      const data = apiError.response?.data as ValidationErrorResponse | undefined
      if (apiError.response?.status === 400 && data?.errors) {
        setServerErrors(data.errors)
      } else {
        showSaveErrorModal()
      }
      return
    } finally {
      setIsSaving(false)
    }
    history.push('/tol-operations/otac-maintenance', { refresh: true })
  }

  const handleCancel = () => {
    setServerErrors([])
    history.push('/tol-operations/otac-maintenance')
  }

  return (
    <Switch>
      <Route exact path="/tol-operations/otac-maintenance">
        <OtacMaintenanceListPage
          schedules={sortOtacByStartAt(schedules)}
          isLoading={isLoading}
          onOpenEdit={() => history.push('/tol-operations/otac-maintenance/form')}
        />
      </Route>
      <Route exact path="/tol-operations/otac-maintenance/form">
        <OtacMaintenanceFormPage
          existingSchedules={schedules}
          isLoading={isLoading}
          isSaving={isSaving}
          serverErrors={serverErrors}
          onClearServerErrors={clearServerErrors}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      </Route>
      <Redirect to="/tol-operations/otac-maintenance" />
    </Switch>
  )
}
